import React, { useState } from "react";
import { Link } from "wouter";
import { useSpring, animated } from "@react-spring/web";
import { useStore } from "../../store/store";

const IntroView: React.FC = () => {
  const { audioManager } = useStore();
  // Hover states for buttons
  const [isHoveringLeft, setIsHoveringLeft] = useState(false);
  const [isHoveringRight, setIsHoveringRight] = useState(false);

  // Custom config for slower and softer animations
  const slowSoftConfig = {
    tension: 120, // Lower tension for softer spring
    friction: 14, // Lower friction for smoother animation
    duration: 2000, // Increase duration to slow down
    // Note: When using duration, stiffness and damping are ignored, so you can omit tension and damping if using duration
  };

  // Animation for Left Title
  const leftTitleSpring = useSpring({
    from: { opacity: 0, transform: "translateX(-50px)" },
    to: { opacity: 1, transform: "translateX(0px)" },
    config: slowSoftConfig,
    delay: 300,
  });

  // Animation for Right Title
  const rightTitleSpring = useSpring({
    from: { opacity: 0, transform: "translateX(100px)" },
    to: { opacity: 1, transform: "translateX(0px)" },
    config: slowSoftConfig,
    delay: 800,
  });

  // Animation for Subtitle
  const subtitleSpring = useSpring({
    from: { opacity: 0, transform: "translateY(50px)" },
    to: { opacity: 1, transform: "translateY(0px)" },
    config: slowSoftConfig,
    delay: 1300,
  });

  // Animation for Action Buttons
  const buttonsSpring = useSpring({
    from: { opacity: 0, transform: "translateY(50px)" },
    to: { opacity: 1, transform: "translateY(0px)" },
    config: slowSoftConfig,
    delay: 1800,
  });

  // Animation for Background Images
  const bgImagesSpring = useSpring({
    from: { opacity: 0, transform: "translateY(50px)" },
    to: { opacity: 1, transform: "translateY(0px)" },
    config: slowSoftConfig,
    delay: 2300,
  });

  // Animation for Left Background Image on Hover
  const leftBgSpring = useSpring({
    transform: isHoveringLeft
      ? "scale(1.05) translateY(-10px)"
      : "scale(1) translateY(0px)",
    config: { tension: 170, friction: 26 }, // Softer hover animation
  });

  // Animation for Right Background Image on Hover
  const rightBgSpring = useSpring({
    transform: isHoveringRight
      ? "scale(1.05) translateY(-10px)"
      : "scale(1) translateY(0px)",
    config: { tension: 170, friction: 26 }, // Softer hover animation
  });

  return (
    <>
      {/* Background Images */}
      <animated.div
        style={bgImagesSpring}
        className="absolute flex items-end justify-between bottom-20 px-5 w-full"
      >
        <animated.div
          style={leftBgSpring}
          className="w-1/3 transform transition-transform duration-2000 ease-in-out origin-bottom-left cursor-pointer"
        >
          <img
            className="w-full"
            src={"/images/intro-screen-bg-1.png"}
            alt="Slider Background"
          />
        </animated.div>
        <animated.div
          style={rightBgSpring}
          className="w-1/3 transform transition-transform duration-2000 ease-in-out origin-bottom-right cursor-pointer"
        >
          <img
            className="w-full"
            src={"/images/intro-screen-bg-2.png"}
            alt="Collection Background"
          />
        </animated.div>
      </animated.div>

      {/* Main Intro Content */}
      <div
        id="Intro"
        className="z-10 min-h-screen w-full absolute top-0 left-0 flex items-center justify-center md:h-full overflow-x-hidden"
      >
        {/* Content Wrapper */}
        <div className="flex flex-col items-center p-4 max-w-2xl text-white py-12 md:py-4">
          {/* Title */}
          <div className="block max-w-xl mb-12">
            <animated.h1 className="font-bold leading-none mb-4 text-4xl md:text-6xl">
              <animated.span style={leftTitleSpring} className="block">
                collectie
              </animated.span>
              <animated.span style={rightTitleSpring} className="block">
                connectie
              </animated.span>
            </animated.h1>
            <animated.p
              style={subtitleSpring}
              className="block text-sm md:text-base"
            >
              Ontdek, beleef en maak kennis met kunst op een persoonlijke <br />
              en intuïtieve manier.
            </animated.p>
          </div>

          {/* Action Buttons */}
          <animated.div
            style={buttonsSpring}
            className="flex flex-col md:flex-row"
          >
            {/* Personalize Section */}
            <div className="flex flex-col md:w-1/2 md:text-right justify-between">
              <div className="flex flex-col">
                {/* Mobile Title */}
                <div className="block md:hidden text-center mb-4">
                  <h2 className="text-base md:text-xl font-bold">
                    PERSONALISEER JE BELEVING
                  </h2>
                </div>
                {/* Desktop Title */}
                <div className={"hidden md:block mb-4"}>
                  <h2 className="text-base md:text-xl font-bold">
                    PERSONALISEER
                    <br />
                    JE BELEVING
                  </h2>
                </div>
                <p className="text-sm md:text-base mb-4">
                  Kies 3 favorieten en laat jouw persoonlijke interesses bepalen
                  hoe je de collectie betreedt.
                </p>
              </div>

              <Link
                onClick={() => {
                  audioManager!.play("click");
                }}
                href="/picker"
                className="flex justify-center items-center p-2 border-2 rounded-full text-white text-sm hover:bg-white hover:text-black hover:border-white transition-colors"
                onMouseEnter={() => setIsHoveringLeft(true)}
                onMouseLeave={() => setIsHoveringLeft(false)}
              >
                <span className="w-5 h-5 mx-2 text-base d-icon d-filter-vertical" />
                <div className="flex-grow text-center">
                  Kies jouw favorieten
                </div>
              </Link>
            </div>

            {/* Divider */}
            <div className="flex flex-col justify-center items-center py-6 md:px-10 md:py-4">
              <div className="h-px w-full bg-white mx-2 md:hidden"></div>
              <span>of</span>
              <div className="h-px w-full bg-white mx-2 md:hidden"></div>
              <div className="w-px h-full bg-white mt-4 hidden md:block"></div>
            </div>

            {/* Explore Collection Section */}
            <div className="flex flex-col md:w-1/2 justify-between">
              <div className="flex flex-col">
                {/* Mobile Title */}
                <div className="block md:hidden text-center mb-4">
                  <h2 className="text-base md:text-xl font-bold">
                    VERKEN DE COLLECTIE
                  </h2>
                </div>
                {/* Desktop Title */}
                <div className={"hidden md:block mb-4"}>
                  <h2 className="text-base md:text-xl font-bold">
                    VERKEN DE
                    <br />
                    COLLECTIE
                  </h2>
                </div>
                <p className="text-sm md:text-base mb-4">
                  Duik in de collectie en laat je inspireren door de kunstwerken
                  die je tegenkomt.
                </p>
              </div>

              <Link
                onClick={() => {
                  audioManager!.play("click");
                  audioManager!.play("background");
                }}
                href="/collection"
                className="flex justify-center content-center items-center p-2 border-2 rounded-full text-white text-sm hover:bg-white hover:text-black hover:border-white transition-colors"
                onMouseEnter={() => setIsHoveringRight(true)}
                onMouseLeave={() => setIsHoveringRight(false)}
              >
                <div className="flex-grow text-center">
                  Direct naar collectie
                </div>
                <span className="w-5 h-5 mx-2 text-base d-icon d-arrow-right" />
              </Link>
            </div>
          </animated.div>
        </div>
      </div>
    </>
  );
};

export default IntroView;
